import {
  LocationQueryValue,
  RouteLocationNormalized,
  createRouter,
  createWebHistory,
} from 'vue-router';
import { useAuthStore } from './stores/authStore';
import userManager from './helperScripts/userManagement/userManager';

/**
 * Checks if the current route contains keycloak parameters like state and session_state
 */
export const routeContainsKeycloakParameters = function (
  route: RouteLocationNormalized
) {
  return (
    route.fullPath.includes('#state') &&
    route.fullPath.includes('&session_state')
  );
};

/**
 * Removes keycloak parameters from route
 */
export const removeKeycloakStateQueryFromGivenUrl = function (
  url: string | undefined
) {
  if (!url) {
    return url;
  }
  const cleanUrl = url
    .replace(/[&?]code=[^&$]*/, '')
    .replace(/[&?]state=[^&$]*/, '')
    .replace(/[&?]session_state=[^&$]*/, '');

  return cleanUrl;
};

//Define routes
const routes = [
  {
    path: '/',
    name: 'home',
    redirect: '/list/general', //Default --> Trial list is shown when opening page on root level
    props: true,
    component: () => import('@/components/Viewer.vue'),
    children: [
      {
        path: 'details',
        name: 'details',
        // Function parameter -> prop annotation allows to use query params (prepended by '?') as props (as is done with route params in other places as well)
        props: (
          route: RouteLocationNormalized
        ): Record<string, LocationQueryValue | LocationQueryValue[]> => ({
          identifierValue: route.query.identifierValue,
          identifierSystem: route.query.identifierSystem,
          studyIdFromUrl: route.query.id,
        }),
        // route level code-splitting
        // this generates a separate chunk for this route
        // which is lazy-loaded when the route is visited.
        component: () => {
          const authStore = useAuthStore();
          const needPatientView = userManager.isUserPatient(authStore);

          if (needPatientView) {
            return import('@/components/TrialDetails/PatientView/PatientTrialDetails.vue');
          }
          return import('@/components/TrialDetails/TrialDetails.vue');
        },
      },
      {
        path: 'changeRequest/',
        name: 'changeRequestDetails',
        props: (
          route: RouteLocationNormalized
        ): Record<string, LocationQueryValue | LocationQueryValue[]> => ({
          identifierValue: route.query.identifierValue,
          identifierSystem: route.query.identifierSystem,
          studyIdFromUrl: route.query.id,
          changeRequestId: route.query.changeRequestId,
        }),
        component: () => import('@/components/TrialDetails/TrialDetails.vue'),
      },
      {
        path: 'list',
        props: true,
        redirect: '/list/general',
        component: () => {
          const authStore = useAuthStore();
          const needPatientView = userManager.isUserPatient(authStore);

          if (needPatientView) {
            return import('./components/TrialsList/PatientView/PatientListFrame.vue');
          }
          return import('./components/TrialsList/ListFrame.vue');
        },
        children: [
          {
            path: 'general',
            name: 'studyList',
            props: { isPersonalStudyList: false },
            component: () =>
              import('./components/TrialsList/TrialsPaginatedList.vue'),
          },
          {
            path: 'favourites',
            name: 'favourites',
            props: { isPersonalStudyList: true },
            component: () =>
              import('./components/TrialsList/TrialsPaginatedList.vue'),
          },
        ],
      },
      {
        path: 'settings',
        name: 'settings',
        props: true,
        component: () => {
          const authStore = useAuthStore();
          const needPatientView = userManager.isUserPatient(authStore);

          if (needPatientView) {
            return import('./components/Settings/PatientOptionsWindow.vue');
          }
          return import('./components/Settings/OptionsWindow.vue');
        },
      },
      {
        path: 'change-requests',
        name: 'changeRequests',
        props: true,
        component: () =>
          import('./components/ChangeRequestList/ChangeRequestPage.vue'),
      },
      {
        path: 'my-change-requests',
        name: 'myChangeRequests',
        props: true,
        component: () =>
          import('./components/ChangeRequestList/UserChangeRequestPage.vue'),
      },
    ],
  },
];

// create router instance
const initRouter = function () {
  // Creating web history here and getting the router via this function in main.ts prevents leaking keycloak state info into the url bar
  const routerHistory = createWebHistory();
  return createRouter({
    history: routerHistory,
    routes,
  });
};

export default initRouter;
